
import { useEffect, useCallback, useRef } from 'react';
import { UseBlogPostsParams, UseBlogPostsReturn } from './types';
import { usePostsState } from './usePostsState';
import { useFetchPosts } from './useFetchPosts';
import { useRefresh } from './useRefresh';
import { BlogPost } from '@/types/blog';

export const useBlogPosts = ({
  initialPage = 1,
  initialPerPage = 10,
  subsequentPerPage,
  searchTerm = '',
  category = '',
  forceRefreshSignal = 0,
  isCategoryChanging = false
}: UseBlogPostsParams = {}): UseBlogPostsReturn => {
  // Initialize posts state management
  const {
    posts,
    setPosts,
    hasMore,
    setHasMore,
    currentPage,
    setCurrentPage,
    totalPosts,
    setTotalPosts,
    addPosts,
    resetPosts
  } = usePostsState(initialPage);
  
  // Use the category and searchTerm directly from props
  const currentCategory = category === '' ? null : category;
  const currentSearchTerm = searchTerm || '';
  const filtersChanged = useRef(false);

  // Setup posts fetching logic
  const {
    loadPosts: fetchPosts,
    loading,
    loadingMore,
    error,
    fetchInProgress,
    lastFetchTime
  } = useFetchPosts({
    initialPage,
    initialPerPage,
    subsequentPerPage,
    currentCategory,
    currentSearchTerm
  });
  
  // Track previous force refresh signal
  const prevForceRefreshSignal = useRef(forceRefreshSignal);
  const isFirstLoad = useRef(true);
  
  // Track if we need to force a refresh for category changes
  const needsCategoryRefresh = useRef(false);
  // Track if we need to force a refresh for search term changes
  const needsSearchRefresh = useRef(false);

  // Process loaded posts
  const loadPosts = useCallback(async (page: number, replace = false, forceRefresh = false) => {
    try {
      console.log(`[useBlogPosts] loadPosts chiamato con page=${page}, replace=${replace}, forceRefresh=${forceRefresh}`);
      console.log(`[useBlogPosts] currentCategory=${currentCategory}, currentSearchTerm=${currentSearchTerm}`);
      
      const newPosts = await fetchPosts(page, replace, forceRefresh);
      
      if (!newPosts || newPosts.length === 0) {
        setHasMore(false);
        return;
      }
      
      const perPage = page === initialPage ? 
        initialPerPage : (subsequentPerPage || initialPerPage);
      
      // Con la nuova logica di filtro (categoria prima, poi testo)
      // il conteggio di hasMore potrebbe essere diverso
      setHasMore(newPosts.length === perPage);
      
      if (replace) {
        // Completely replace posts
        console.log(`[useBlogPosts] Replacing posts with ${newPosts.length} new posts`);
        setPosts(newPosts);
        setTotalPosts(newPosts.length);
      } else {
        // Add new posts to existing ones
        console.log(`[useBlogPosts] Adding ${newPosts.length} posts to existing ${posts.length}`);
        addPosts(newPosts);
      }
    } catch (err) {
      console.error("Error in loadPosts:", err);
      setHasMore(false);
    }
  }, [fetchPosts, initialPage, initialPerPage, subsequentPerPage, setPosts, setHasMore, addPosts, setTotalPosts, posts.length, currentCategory, currentSearchTerm]);

  // Initialize refetch and pagination reset functionality
  const { resetPagination, refetch } = useRefresh({
    initialPage,
    loadPosts,
    resetPosts,
    setHasMore,
    setCurrentPage,
    setLoading: () => {} // We're using loading from useFetchPosts
  });

  // Handle category changes
  useEffect(() => {
    // When category changes externally
    console.log(`Category state: "${category}"`);
    
    // Clear posts and prepare for category change
    setPosts([]);
    setHasMore(true);
    needsCategoryRefresh.current = true;
    filtersChanged.current = true;
  }, [category, setPosts, setHasMore]);

  // Handle search term changes - only applied after category filter
  useEffect(() => {
    // When search term changes externally
    console.log(`Search term state: "${searchTerm}"`);
    
    needsSearchRefresh.current = true;
    filtersChanged.current = true;
  }, [searchTerm]);

  // Handle external category change flag
  useEffect(() => {
    if (isCategoryChanging) {
      console.log('Category is actively changing - clearing posts immediately and setting loading');
      setPosts([]);
      setHasMore(true);
    }
  }, [isCategoryChanging, setPosts, setHasMore]);

  // Initial load and filter changes
  useEffect(() => {
    if (!fetchInProgress && (
      (lastFetchTime === 0) || 
      filtersChanged.current || 
      needsCategoryRefresh.current || 
      needsSearchRefresh.current
    )) {
      // Apply delay for search filter but not for category filter
      const delay = (needsSearchRefresh.current && !needsCategoryRefresh.current) ? 150 : 0;
      
      setTimeout(() => {
        console.log(`Triggering fetch due to filter change or initial load after ${delay}ms delay`);
        console.log(`Current filters: category=${currentCategory}, search="${currentSearchTerm}"`);
        
        // Force refresh when category changes
        const forceRefresh = needsCategoryRefresh.current || filtersChanged.current;
        loadPosts(initialPage, true, forceRefresh);
        
        filtersChanged.current = false;
        needsCategoryRefresh.current = false;
        needsSearchRefresh.current = false;
      }, delay);
    }
  }, [currentSearchTerm, currentCategory, initialPage, loadPosts, fetchInProgress, lastFetchTime, filtersChanged]);

  // Traccia se abbiamo già gestito questo segnale di refresh
  const handledRefreshSignals = useRef<number[]>([]);
  
  // Handle force refresh signal and category change count
  useEffect(() => {
    // Se è il primo caricamento, imposta solo il valore di riferimento
    if (isFirstLoad.current) {
      prevForceRefreshSignal.current = forceRefreshSignal;
      isFirstLoad.current = false;
      return;
    }
    
    // Se il segnale è cambiato e non l'abbiamo già gestito
    if (forceRefreshSignal !== prevForceRefreshSignal.current && 
        !handledRefreshSignals.current.includes(forceRefreshSignal)) {
      
      console.log(`Force refresh signal changed: ${prevForceRefreshSignal.current} -> ${forceRefreshSignal}`);
      
      // Aggiorna il valore di riferimento
      prevForceRefreshSignal.current = forceRefreshSignal;
      
      // Aggiungi questo segnale alla lista dei segnali gestiti
      handledRefreshSignals.current.push(forceRefreshSignal);
      
      // Limita la dimensione dell'array per evitare problemi di memoria
      if (handledRefreshSignals.current.length > 10) {
        handledRefreshSignals.current.shift();
      }
      
      // Resetta la paginazione e carica i post
      resetPagination();
      loadPosts(initialPage, true, true);
    }
  }, [forceRefreshSignal, initialPage, loadPosts, resetPagination]);

  // Fetch more posts for infinite scrolling
  const fetchMore = useCallback(async () => {
    if (loadingMore || !hasMore || loading) {
      return;
    }
    
    console.log('Fetching more posts for infinite scroll');
    const nextPage = currentPage + 1;
    await loadPosts(nextPage, false);
    setCurrentPage(nextPage);
  }, [loadingMore, hasMore, currentPage, loadPosts, loading, setCurrentPage]);

  return {
    posts,
    loading,
    error,
    fetchMore,
    hasMore,
    refetch,
    totalPosts,
    currentPage,
    loadingMore
  };
};
